import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type { CartLine, Personalisation, Product, PublicConfig, SiteSettings } from "../shared/types";
import { findVariant, formatAud, validatePersonalisation } from "../shared/pricing";
import { apiJson } from "./api";
import { useCart } from "./cart";
import { Link, ProductCard, StatusMessage, navigate, usePageMeta } from "./components";

export function HomePage({ products }: { products: Product[] }) {
  usePageMeta("Pressed Studio Signs | Personalised fabric signage", "Handmade personalised fabric signage for weddings, celebrations and meaningful occasions.");
  const active = products.filter((product) => product.active);
  return <>
    <section className="hero">
      <div className="hero__copy">
        <p className="eyebrow">Personalised fabric signage</p>
        <h1>Made for the moments worth keeping.</h1>
        <p>Handmade fabric signs for weddings, celebrations and meaningful occasions. Personalised with your words.</p>
        <div className="button-row">
          <Link href="/shop" className="button">Shop signs</Link>
          <Link href="/custom-signs" className="text-link">Create something custom ↗</Link>
        </div>
      </div>
      <figure className="hero__media">
        <img src="/media/products/pressed-studio-chiffon-garden-full.jpg" alt="Personalised chiffon sign displayed among white flowers" fetchPriority="high" />
        <figcaption>Personalised fabric signage</figcaption>
      </figure>
    </section>

    <section className="section collection-section">
      <div className="section-heading split-heading">
        <div><p className="eyebrow">The collection</p><h2>Find your piece.</h2></div>
        <Link href="/shop" className="text-link">View collection →</Link>
      </div>
      <div className="product-grid">
        {active.slice(0, 5).map((product, index) => <ProductCard key={product.id} product={product} priority={index < 2} />)}
      </div>
    </section>

    <section className="section section--bone">
      <div className="section-heading"><p className="eyebrow">Find your material</p><h2>Different textures.<br />Your words.</h2></div>
      <div className="material-grid">
        <article>
          <img src="/media/products/pressed-studio-linen-hanging-sign-01-full.jpg" alt="Linen sign hanging outdoors" loading="lazy" />
          <p className="index">01</p><h3>Linen ↗</h3><p>Tactile and understated.</p>
        </article>
        <article>
          <img src="/media/products/pressed-studio-lace-chair-signs-02-detail.jpg" alt="Close detail of chiffon and lace signage" loading="lazy" />
          <p className="index">02</p><h3>Chiffon + lace ↗</h3><p>Light, layered and delicate.</p>
        </article>
        <article>
          <img src="/media/products/pressed-studio-satin-chair-signs-02-detail.jpg" alt="Lettering detail on an ivory satin chair sign" loading="lazy" />
          <p className="index">03</p><h3>Satin ↗</h3><p>Smooth, luminous and fluid.</p>
        </article>
      </div>
    </section>

    <section className="section process-section">
      <div className="section-heading"><p className="eyebrow">How it works</p><h2>From your wording<br />to the finished piece.</h2></div>
      <ol className="process-grid">
        <li><span>01</span><h3>Choose your piece</h3><p>Select the material, size and finish that suits the occasion.</p></li>
        <li><span>02</span><h3>Personalise the details</h3><p>Add the wording, names, date or lettering style exactly as required.</p></li>
        <li><span>03</span><h3>Check every word</h3><p>Review spelling, dates and options before adding the piece to your bag.</p></li>
        <li><span>04</span><h3>Made for you</h3><p>Each piece is prepared individually and sent from Australia.</p></li>
      </ol>
    </section>

    <section className="section custom-callout">
      <div>
        <p className="eyebrow">Custom signs</p>
        <h2>Have something else in mind?</h2>
        <p>Share the occasion, wording and approximate size. The studio will confirm what is possible before any custom work begins.</p>
        <Link href="/custom-signs" className="text-link">Start a custom enquiry →</Link>
      </div>
      <img src="/media/products/pressed-studio-chiffon-hanging-sign-01-full.jpg" alt="Long chiffon and lace sign hanging outdoors" loading="lazy" />
    </section>

    <section className="section studio-story">
      <div>
        <p className="eyebrow">The studio</p>
        <h2>A small studio.<br />A personal piece.</h2>
        <p>Pressed Studio Signs creates handmade, personalised fabric signage for weddings, events and meaningful occasions.</p>
        <Link href="/about" className="text-link">Read the story →</Link>
      </div>
      <div className="studio-story__image-pair">
        <img src="/media/products/pressed-studio-chiffon-garden-detail.jpg" alt="Detail of lettering on a chiffon fabric sign" loading="lazy" />
        <img src="/media/products/pressed-studio-lace-chair-signs-03-styled.jpg" alt="Chiffon chair signs styled at a wedding table" loading="lazy" />
      </div>
    </section>

    <section className="final-cta">
      <p className="eyebrow">Pressed Studio Signs</p>
      <h2>Your words.<br />A piece to keep.</h2>
      <div className="button-row"><Link href="/shop" className="button">Shop the collection</Link><Link href="/custom-signs" className="button button--outline">Create something custom</Link></div>
    </section>
  </>;
}

export function ShopPage({ products }: { products: Product[] }) {
  usePageMeta("Shop personalised fabric signs | Pressed Studio Signs", "Shop handmade linen, chiffon, lace and satin signs personalised with your wording.");
  const [category, setCategory] = useState("All");
  const active = products.filter((product) => product.active && (category === "All" || product.category === category));
  return <section className="page section">
    <header className="page-heading">
      <p className="eyebrow">The collection</p>
      <h1>Find your piece.</h1>
      <p>Choose a fabric, size and finish, then add your exact wording.</p>
    </header>
    <div className="filter-row" role="group" aria-label="Filter products">
      {["All", "Fabric Signs", "Chair Signs"].map((item) => <button key={item} type="button" className={category === item ? "is-active" : ""} onClick={() => setCategory(item)}>{item}</button>)}
    </div>
    <div className="product-grid product-grid--shop">{active.map((product) => <ProductCard key={product.id} product={product} />)}</div>
  </section>;
}

function MediaGallery({ product }: { product: Product }) {
  const [selected, setSelected] = useState(0);
  const media = product.media;
  const active = media[selected] || media[0];
  return <div className="gallery">
    <div className="gallery__stage">
      {active?.kind === "video" ? (
        <video src={active.url} poster={active.poster} controls playsInline preload="metadata" aria-label={active.alt} />
      ) : active ? <img src={active.url} alt={active.alt} /> : null}
    </div>
    {media.length > 1 && <div className="gallery__thumbs" role="list">
      {media.map((item, index) => <button key={item.id} type="button" aria-label={`View ${item.alt}`} aria-current={selected === index} onClick={() => setSelected(index)}>
        {item.kind === "video" ? <span className="video-thumb"><img src={item.poster} alt="" /><span>Play</span></span> : <img src={item.url} alt="" />}
      </button>)}
    </div>}
  </div>;
}

export function ProductPage({ product, related }: { product: Product; related: Product[] }) {
  usePageMeta(product.seoTitle, product.seoDescription);
  const cart = useCart();
  const editId = new URLSearchParams(window.location.search).get("edit");
  const editing = editId ? cart.lines.find((line) => line.id === editId && line.productId === product.id) : undefined;
  const initialVariant = product.variants.find((item) => item.id === editing?.variantId && item.available) || product.variants.find((item) => item.available);
  const [selections, setSelections] = useState<Record<string, string>>(initialVariant ? { ...initialVariant.selections } : {});
  const [quantity, setQuantity] = useState(editing?.quantity || 1);
  const [personalisation, setPersonalisation] = useState<Personalisation>(editing?.personalisation || {
    style: "",
    wording: "",
    wordingOne: "",
    wordingTwo: "",
    requestedDate: "",
    note: "",
    wordingConfirmed: false
  });
  const [error, setError] = useState("");
  const variant = useMemo(() => findVariant(product, selections), [product, selections]);

  function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      if (!variant?.available) throw new Error("That combination is not currently available.");
      validatePersonalisation(product, personalisation);
      const line = { productId: product.id, variantId: variant.id, quantity, personalisation };
      if (editing && editId) cart.updateLine(editId, line);
      else cart.addLine(line);
      navigate("/cart");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Check the selected options and wording.");
    }
  }

  const styleCount = product.styleSelector === "design" ? 8 : 7;
  return <>
    <div className="breadcrumbs"><Link href="/">Home</Link><span>/</span><Link href="/shop">Shop</Link><span>/</span><span>{product.name}</span></div>
    <section className="product-page">
      <MediaGallery product={product} />
      <form className="product-form" onSubmit={submit}>
        <p className="eyebrow">{product.material}</p>
        <h1>{product.name}</h1>
        <p className="product-price">{variant ? `${formatAud(variant.priceCents)} AUD` : "Unavailable"}</p>
        <p>{product.shortDescription}</p>

        <fieldset className="form-step">
          <legend><span>01</span> Choose your piece</legend>
          <div className="field-grid">
            {product.options.map((option) => <label key={option.id} className="field">
              <span>{option.label}</span>
              <select value={selections[option.id] || ""} required={option.required} onChange={(event) => setSelections((current) => ({ ...current, [option.id]: event.target.value }))}>
                <option value="">Choose {option.label.toLowerCase()}</option>
                {option.values.map((value) => <option key={value.id} value={value.id}>{value.label}</option>)}
              </select>
            </label>)}
          </div>
          <details className="inline-details"><summary>See the size, lettering and finish guides</summary><div className="guide-grid">{product.guides.map((item) => <figure key={item.id}><img src={item.url} alt={item.alt} loading="lazy" /><figcaption>{item.alt}</figcaption></figure>)}</div></details>
        </fieldset>

        <fieldset className="form-step">
          <legend><span>02</span> Make it yours</legend>
          <p className="form-intro">{product.personalisationPrompt}</p>
          <label className="field">
            <span>{product.styleSelector === "design" ? "Design number" : "Lettering style"}</span>
            <select value={personalisation.style} required onChange={(event) => setPersonalisation({ ...personalisation, style: event.target.value })}>
              <option value="">Choose {product.styleSelector === "design" ? "design" : "style"} 1 to {styleCount}</option>
              {Array.from({ length: styleCount }, (_, index) => String(index + 1)).map((value) => <option key={value} value={value}>{product.styleSelector === "design" ? "Design" : "Style"} {value}</option>)}
            </select>
          </label>
          {product.personalisationMode === "pair" ? <div className="field-grid">
            <label className="field"><span>First sign wording</span><input maxLength={256} required value={personalisation.wordingOne || ""} placeholder="For example: Just" onChange={(event) => setPersonalisation({ ...personalisation, wordingOne: event.target.value })} /></label>
            <label className="field"><span>Second sign wording</span><input maxLength={256} required value={personalisation.wordingTwo || ""} placeholder="For example: Married" onChange={(event) => setPersonalisation({ ...personalisation, wordingTwo: event.target.value })} /></label>
          </div> : <label className="field"><span>Exact wording</span><textarea maxLength={256} required value={personalisation.wording || ""} placeholder="Enter names, dates and wording exactly as they should appear" onChange={(event) => setPersonalisation({ ...personalisation, wording: event.target.value })} /><small>{(personalisation.wording || "").length}/256 characters</small></label>}
          <label className="field"><span>Requested-by date <em>optional</em></span><input type="date" value={personalisation.requestedDate || ""} onChange={(event) => setPersonalisation({ ...personalisation, requestedDate: event.target.value })} /><small>A requested date is not a confirmed delivery date.</small></label>
          <details className="inline-details"><summary>Add a note <em>optional</em></summary><label className="field"><span>Additional requirements</span><textarea maxLength={2000} value={personalisation.note || ""} onChange={(event) => setPersonalisation({ ...personalisation, note: event.target.value })} /></label></details>
        </fieldset>

        <fieldset className="form-step">
          <legend><span>03</span> Check the details</legend>
          <div className="selection-summary">
            <p className="eyebrow">Your selection</p>
            <strong>{variant?.label || "Choose the available options"}</strong>
            <dl>
              <div><dt>{product.styleSelector === "design" ? "Design" : "Lettering"}</dt><dd>{personalisation.style || "Not selected"}</dd></div>
              {product.personalisationMode === "pair" ? <><div><dt>First sign</dt><dd>{personalisation.wordingOne || "No wording entered"}</dd></div><div><dt>Second sign</dt><dd>{personalisation.wordingTwo || "No wording entered"}</dd></div></> : <div><dt>Wording</dt><dd>{personalisation.wording || "No wording entered"}</dd></div>}
            </dl>
            <p>{quantity} × {variant ? formatAud(variant.priceCents) : "Unavailable"} AUD</p>
          </div>
          <label className="check-field"><input type="checkbox" checked={personalisation.wordingConfirmed} onChange={(event) => setPersonalisation({ ...personalisation, wordingConfirmed: event.target.checked })} /> <span>I have checked my wording and selected options.</span></label>
          <div className="purchase-row">
            <label className="field quantity-field"><span>Quantity</span><input type="number" min="1" max="10" value={quantity} onChange={(event) => setQuantity(Number(event.target.value))} /></label>
            <button className="button" type="submit" disabled={!variant?.available}>{editing ? "Save changes" : variant?.available ? "Add to bag" : "Currently unavailable"}</button>
          </div>
          {error && <StatusMessage type="error">{error}</StatusMessage>}
        </fieldset>
        <details className="product-details"><summary>About this piece</summary><p>{product.description}</p></details>
      </form>
    </section>

    <section className="section related-section">
      <div className="split-heading"><div><p className="eyebrow">You may also like</p><h2>Related pieces</h2></div><Link href="/shop" className="text-link">View collection →</Link></div>
      <div className="product-grid">{related.slice(0, 4).map((item) => <ProductCard key={item.id} product={item} />)}</div>
    </section>
  </>;
}

export function CartPage({ products, settings, config }: { products: Product[]; settings: SiteSettings; config: PublicConfig }) {
  usePageMeta("Your bag | Pressed Studio Signs", "Review your personalised Pressed Studio Signs order.");
  const cart = useCart();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const resolved = cart.lines.map((line) => ({ line, product: products.find((item) => item.id === line.productId) })).filter((item) => item.product) as Array<{ line: CartLine; product: Product }>;
  const subtotal = resolved.reduce((sum, { line, product }) => sum + (product.variants.find((variant) => variant.id === line.variantId)?.priceCents || 0) * line.quantity, 0);
  const hasUnavailable = resolved.some(({ line, product }) => !product.variants.some((variant) => variant.id === line.variantId && variant.available));

  async function checkout() {
    setBusy(true);
    setError("");
    try {
      const result = await apiJson<{ url: string }>("/api/checkout", { method: "POST", body: JSON.stringify({ lines: cart.lines }) });
      window.location.assign(result.url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Checkout could not be started.");
      setBusy(false);
    }
  }

  if (!resolved.length) return <section className="page section empty-state"><p className="eyebrow">Your bag</p><h1>Your bag is empty.</h1><p>Choose a piece and add the wording that will make it yours.</p><Link href="/shop" className="button">Shop signs</Link></section>;

  return <section className="page section cart-page">
    <header className="page-heading"><p className="eyebrow">Your bag</p><h1>Review every detail.</h1><p>Check spelling, dates and options before continuing to payment.</p></header>
    <div className="cart-layout">
      <div className="cart-lines">
        {resolved.map(({ line, product }) => {
          const variant = product.variants.find((item) => item.id === line.variantId);
          const image = product.media.find((item) => item.kind === "image");
          return <article className="cart-line" key={line.id}>
            {image && <img src={image.url} alt={image.alt} />}
            <div className="cart-line__body">
              <p className="eyebrow">{product.material}</p>
              <h2>{product.name}</h2>
              <p>{variant?.label}</p>
              <dl>
                <div><dt>{product.styleSelector === "design" ? "Design" : "Lettering"}</dt><dd>{line.personalisation.style}</dd></div>
                {product.personalisationMode === "pair" ? <><div><dt>First sign</dt><dd>{line.personalisation.wordingOne}</dd></div><div><dt>Second sign</dt><dd>{line.personalisation.wordingTwo}</dd></div></> : <div><dt>Wording</dt><dd>{line.personalisation.wording}</dd></div>}
                {line.personalisation.requestedDate && <div><dt>Requested date</dt><dd>{line.personalisation.requestedDate}</dd></div>}
              </dl>
              <div className="cart-line__actions">
                <label className="field quantity-field"><span>Quantity</span><input type="number" min="1" max="10" value={line.quantity} onChange={(event) => cart.setQuantity(line.id, Number(event.target.value))} /></label>
                <Link href={`/product/${product.slug}?edit=${line.id}`} className="text-link">Edit details</Link>
                <button type="button" className="text-button" onClick={() => cart.removeLine(line.id)}>Remove</button>
              </div>
            </div>
            <strong>{variant ? formatAud(variant.priceCents * line.quantity) : "Unavailable"}</strong>
          </article>;
        })}
      </div>
      <aside className="order-summary">
        <p className="eyebrow">Order summary</p>
        <div><span>Subtotal</span><strong>{formatAud(subtotal)}</strong></div>
        <div><span>Shipping</span><span>{config.shippingConfigured && settings.shippingRateCents !== null ? formatAud(settings.shippingRateCents) : "Confirm before launch"}</span></div>
        <p className="help-text">{settings.turnaroundMessage}</p>
        {!config.checkoutEnabled && <StatusMessage>Online payment remains safely closed until the approved shipping rate, policies and live Stripe account are connected.</StatusMessage>}
        {hasUnavailable && <StatusMessage type="error">A configuration in your bag is no longer available. Remove it or choose another option before checkout.</StatusMessage>}
        {error && <StatusMessage type="error">{error}</StatusMessage>}
        <button type="button" className="button" disabled={!config.checkoutEnabled || hasUnavailable || busy} onClick={checkout}>{busy ? "Opening secure checkout…" : "Continue to secure checkout"}</button>
        {!config.checkoutEnabled && <Link href="/custom-signs" className="text-link">Send an enquiry instead →</Link>}
      </aside>
    </div>
  </section>;
}

function Turnstile({ siteKey, onToken }: { siteKey: string | null; onToken: (token: string) => void }) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!siteKey || !root.current) return;
    let widgetId = "";
    const render = () => {
      if (window.turnstile && root.current && !widgetId) {
        widgetId = window.turnstile.render(root.current, { sitekey: siteKey, callback: onToken, "expired-callback": () => onToken("") });
      }
    };
    const existing = document.querySelector<HTMLScriptElement>('script[src*="challenges.cloudflare.com/turnstile"]');
    if (existing) render();
    else {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = render;
      document.head.appendChild(script);
    }
    return () => { if (widgetId && window.turnstile) window.turnstile.remove(widgetId); };
  }, [siteKey, onToken]);
  return siteKey ? <div ref={root} className="turnstile" /> : null;
}

export function EnquiryPage({ config }: { config: PublicConfig }) {
  usePageMeta("Custom fabric signs | Pressed Studio Signs", "Tell Pressed Studio Signs about a custom fabric sign for your event, business or occasion.");
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const data = new FormData(event.currentTarget);
      data.set("turnstileToken", token);
      await apiJson<{ id: string }>("/api/enquiries", { method: "POST", body: data });
      event.currentTarget.reset();
      setStatus({ type: "success", message: "Thank you. Your enquiry has been received." });
    } catch (caught) {
      setStatus({ type: "error", message: caught instanceof Error ? caught.message : "The enquiry could not be sent." });
    } finally {
      setBusy(false);
    }
  }

  return <section className="page section enquiry-page">
    <div className="enquiry-intro"><p className="eyebrow">Custom signs</p><h1>Tell us what you are imagining.</h1><p>Share the occasion, timing and idea. Jamie-Lee will confirm what is possible and provide the next step.</p><img src="/media/products/pressed-studio-chiffon-garden-full.jpg" alt="Chiffon and lace event sign among flowers" /></div>
    <form className="enquiry-form" onSubmit={submit} encType="multipart/form-data">
      {status && <StatusMessage type={status.type}>{status.message}</StatusMessage>}
      <div className="field-grid">
        <label className="field"><span>Name *</span><input name="name" maxLength={120} autoComplete="name" required /></label>
        <label className="field"><span>Email *</span><input name="email" type="email" maxLength={190} autoComplete="email" required /></label>
        <label className="field"><span>Phone <em>optional</em></span><input name="phone" type="tel" maxLength={40} autoComplete="tel" /></label>
        <label className="field"><span>Occasion or sign type</span><input name="occasion" maxLength={180} placeholder="Wedding welcome sign, chair signs, business sign" /></label>
        <label className="field"><span>Event date</span><input name="eventDate" type="date" /></label>
        <label className="field"><span>Required delivery date</span><input name="requiredDate" type="date" /></label>
        <label className="field"><span>Approximate dimensions</span><input name="dimensions" maxLength={120} placeholder="For example: 70cm × 100cm" /></label>
        <label className="field"><span>Material</span><select name="material"><option value="">Not sure yet</option><option>Linen</option><option>Chiffon + lace</option><option>Satin</option></select></label>
        <label className="field"><span>Colour or style</span><input name="style" maxLength={180} /></label>
        <label className="field"><span>Quantity</span><input name="quantity" type="number" min="1" max="100" /></label>
      </div>
      <label className="field"><span>Wording</span><textarea name="wording" maxLength={1000} placeholder="Enter wording exactly as it may appear" /></label>
      <label className="field"><span>What would you like made? *</span><textarea name="details" maxLength={3000} required placeholder="Describe the piece, finish and any other important details." /></label>
      <label className="field"><span>Reference image or PDF <em>optional, maximum 8MB</em></span><input name="attachment" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" /></label>
      <label className="check-field"><input name="consent" type="checkbox" required /> <span>I consent to Pressed Studio Signs using these details to respond to this enquiry.</span></label>
      <input className="honey" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <Turnstile siteKey={config.turnstileSiteKey} onToken={setToken} />
      <button type="submit" className="button" disabled={busy}>{busy ? "Sending…" : "Send enquiry"}</button>
    </form>
  </section>;
}

export function AboutPage() {
  usePageMeta("About Pressed Studio Signs", "Meet Pressed Studio Signs, an Australian studio making personalised fabric signage.");
  return <section className="page section prose-page">
    <header className="page-heading"><p className="eyebrow">The studio</p><h1>A personal piece for a meaningful occasion.</h1></header>
    <div className="editorial-split">
      <div><h2>Made by Jamie-Lee.</h2><p>Pressed Studio Signs is an Australian small business creating personalised fabric signage for weddings, celebrations, events, businesses and special occasions.</p><p>Each piece is made to order using the selected fabric, size, finish and exact wording. The result is soft, tactile signage designed to belong to the occasion rather than compete with it.</p><p>If the collection does not quite fit your idea, send a custom enquiry. Dimensions, materials and timing can be discussed before anything is confirmed.</p></div>
      <img src="/media/products/pressed-studio-chiffon-hanging-sign-01-full.jpg" alt="Personalised chiffon sign hanging in a garden" />
    </div>
  </section>;
}

const faqs = [
  ["How do I personalise a sign?", "Choose the product options, select a design or lettering style, and enter the wording exactly as it should appear."],
  ["Can I request another size or design?", "Yes. Use the custom enquiry form for anything outside the listed options. What is possible and the price will be confirmed before you order."],
  ["Where do you deliver?", "Orders are sent from Australia. Available destinations, rates and delivery details are confirmed before the site accepts live payment."],
  ["Can personalised orders be returned?", "Personalised pieces are made specifically for each customer. Read the published returns policy before ordering and contact the studio promptly if there is a problem."],
  ["How should I enter names and dates?", "Enter all wording exactly as it should appear, including spelling, capitalisation and dates. Check it again in your bag before checkout."]
];

export function FaqPage() {
  usePageMeta("Questions | Pressed Studio Signs", "Answers about personalisation, custom signs, delivery and ordering.");
  return <section className="page section faq-page"><header className="page-heading"><p className="eyebrow">Questions</p><h1>Before you order.</h1></header><div className="faq-list">{faqs.map(([question, answer]) => <details key={question}><summary>{question}</summary><p>{answer}</p></details>)}</div><div className="soft-panel"><h2>Still unsure?</h2><p>Send the studio your idea and required date before ordering.</p><Link href="/custom-signs" className="text-link">Start a custom enquiry →</Link></div></section>;
}

export function ContactPage({ config }: { config: PublicConfig }) {
  return <EnquiryPage config={config} />;
}

export function PolicyPage({ type, settings }: { type: string; settings: SiteSettings }) {
  const map: Record<string, [string, string]> = {
    shipping: ["Shipping", settings.shippingPolicy],
    returns: ["Returns", settings.returnsPolicy],
    privacy: ["Privacy", settings.privacyPolicy],
    terms: ["Terms", settings.termsPolicy]
  };
  const [title, content] = map[type] || ["Policy", "This policy is not available."];
  usePageMeta(`${title} | Pressed Studio Signs`, `${title} information for Pressed Studio Signs customers.`);
  return <section className="page section prose-page"><header className="page-heading"><p className="eyebrow">Information</p><h1>{title}</h1></header><div className="policy-copy">{content.split(/\n+/).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div></section>;
}

type ConfirmationOrder = {
  orderNumber: string;
  status: string;
  totalCents: number;
  currency: string;
  email: string;
  items: Array<{ name: string; variant: string; quantity: number; lineTotalCents: number; personalisation: Personalisation }>;
};

export function SuccessPage() {
  usePageMeta("Order received | Pressed Studio Signs", "Your Pressed Studio Signs order confirmation.");
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get("order");
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState<ConfirmationOrder | null>(null);
  const [error, setError] = useState("");
  const cart = useCart();

  useEffect(() => {
    if (!orderId || !sessionId) { setError("The confirmation link is incomplete."); return; }
    apiJson<ConfirmationOrder>(`/api/orders/${encodeURIComponent(orderId)}?session_id=${encodeURIComponent(sessionId)}`)
      .then((result) => { setOrder(result); if (result.status === "paid" || result.status === "processing") cart.clear(); })
      .catch((caught) => setError(caught instanceof Error ? caught.message : "The order could not be verified."));
  }, [orderId, sessionId]);

  return <section className="page section confirmation-page">
    <p className="eyebrow">Order confirmation</p>
    {!order && !error && <p>Verifying your payment…</p>}
    {error && <StatusMessage type="error">{error} If payment was completed, keep your Stripe receipt and contact the studio.</StatusMessage>}
    {order && <><h1>Thank you. Your order is in.</h1><p>Order <strong>{order.orderNumber}</strong> has been recorded for <strong>{order.email}</strong>.</p><div className="confirmation-card"><div><span>Status</span><strong>{order.status}</strong></div><div><span>Total</span><strong>{formatAud(order.totalCents)} AUD</strong></div>{order.items.map((item, index) => <article key={`${item.name}-${index}`}><h2>{item.name}</h2><p>{item.variant} × {item.quantity}</p><p>{item.personalisation.wording || `${item.personalisation.wordingOne} / ${item.personalisation.wordingTwo}`}</p></article>)}</div><Link href="/" className="text-link">Return home →</Link></>}
  </section>;
}

export function NotFoundPage() {
  usePageMeta("Page not found | Pressed Studio Signs", "The requested page could not be found.");
  return <section className="page section empty-state"><p className="eyebrow">404</p><h1>This page has moved.</h1><p>Return to the collection to find your piece.</p><Link href="/shop" className="button">Shop signs</Link></section>;
}
